import React, { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ListBulletIcon } from '@heroicons/react/24/outline';

interface CalendarHeaderProps {
  currentDate: Date;
  calendarView: 'month' | 'week';
  setCalendarView: (view: 'month' | 'week') => void;
  selectedWeek: number;
  setSelectedWeek: (week: number) => void;
  selectedYear: number;
  handlePrev: () => void;
  handleNext: () => void;
  handleToday: () => void;
  setIsWeeklyModalOpen: (open: boolean) => void;
}

export const CalendarHeader: React.FC<CalendarHeaderProps> = ({
  currentDate,
  calendarView,
  setCalendarView,
  selectedWeek,
  setSelectedWeek,
  selectedYear,
  handlePrev,
  handleNext,
  handleToday,
  setIsWeeklyModalOpen
}) => {
  const [isWeekSelectorOpen, setIsWeekSelectorOpen] = useState(false);
  const [weekSearchQuery, setWeekSearchQuery] = useState('');

  return (
    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6 sticky top-0 bg-slate-50/90 backdrop-blur-sm z-20 py-2 border-b border-slate-100">
      <div>
        <span className="text-[10px] font-black uppercase tracking-widest text-[#d1a228] bg-amber-50 px-2.5 py-1 rounded-full">
          Predicción & Plan de Planta
        </span>
        
        {calendarView === 'month' ? (
          <h2 className="text-2xl font-bold text-gray-900 mt-2">
            {currentDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }).toUpperCase()}
          </h2>
        ) : (
          <h2 className="text-2xl font-bold text-gray-900 mt-2">
            Semana {selectedWeek} ({selectedYear})
          </h2>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto justify-between lg:justify-start">
        {/* Botón Planificador Semanal */}
        <button
          onClick={() => setIsWeeklyModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-black bg-[#E2B53F] hover:bg-[#c99f30] text-gray-900 rounded-xl transition-all shadow-md hover:shadow-lg border border-[#c99f30]/30"
          title="Ver y planificar cortes de la semana"
        >
          <ListBulletIcon className="h-5 w-5 text-gray-900" />
          Planificador Semanal de Cortes
        </button>

        <div className="flex items-center space-x-2">
          <button 
            onClick={handlePrev} 
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200 bg-white"
          >
            <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
          </button>
          
          <button 
            onClick={handleToday}
            className="px-4 py-2 bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 font-bold rounded-lg text-sm transition-all shadow-sm"
          >
            Hoy
          </button>
          
          <button 
            onClick={handleNext} 
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200 bg-white"
          >
            <ChevronRightIcon className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        {calendarView === 'week' && (
          <div className="relative">
            {/* Backdrop para cerrar al hacer clic afuera */}
            {isWeekSelectorOpen && (
              <div className="fixed inset-0 z-30" onClick={() => { setIsWeekSelectorOpen(false); setWeekSearchQuery(''); }} />
            )}
            
            {/* Botón del selector */}
            <button
              onClick={() => setIsWeekSelectorOpen(prev => !prev)}
              className="bg-white border border-gray-200 text-gray-700 font-bold px-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-[#E2B53F] flex items-center gap-1.5 shadow-sm hover:bg-slate-50 transition-colors z-40 relative"
            >
              <span>Semana {selectedWeek}</span>
              <span className="text-[10px] text-gray-400">▼</span>
            </button>

            {/* Contenedor del Dropdown */}
            {isWeekSelectorOpen && (
              <div className="absolute right-0 mt-1.5 w-48 bg-white border border-gray-200 rounded-xl shadow-xl z-40 animate-in fade-in slide-in-from-top-1 duration-150 p-2 space-y-2">
                {/* Entrada de Búsqueda */}
                <input
                  type="text"
                  placeholder="Buscar semana..."
                  value={weekSearchQuery}
                  onChange={(e) => setWeekSearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className="w-full text-xs font-semibold px-2.5 py-1.5 bg-slate-50 rounded-lg border border-gray-200 focus:ring-1 focus:ring-[#E2B53F] outline-none"
                  autoFocus
                />

                {/* Lista de semanas filtradas */}
                <div className="max-h-52 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200 space-y-0.5">
                  {Array.from({ length: 53 }, (_, i) => i + 1)
                    .filter(w => {
                      if (!weekSearchQuery.trim()) return true;
                      return w.toString().includes(weekSearchQuery.trim());
                    })
                    .map((w) => (
                      <button
                        key={`week-select-item-${w}`}
                        onClick={() => {
                          setSelectedWeek(w);
                          setIsWeekSelectorOpen(false);
                          setWeekSearchQuery('');
                        }}
                        className={`w-full text-left text-xs font-semibold px-2.5 py-2 rounded-lg transition-colors flex justify-between items-center ${
                          w === selectedWeek
                            ? 'bg-[#E2B53F]/10 text-gray-900 font-extrabold'
                            : 'text-gray-600 hover:bg-gray-100/70 hover:text-gray-900'
                        }`}
                      >
                        <span>Semana {w}</span>
                        {w === selectedWeek && (
                          <span className="text-[10px] text-[#d1a228] font-black">✓</span>
                        )}
                      </button>
                    ))}
                  
                  {/* Mensaje si no hay resultados */}
                  {Array.from({ length: 53 }, (_, i) => i + 1).filter(w => w.toString().includes(weekSearchQuery.trim())).length === 0 && (
                    <div className="text-center py-4 text-[10px] text-gray-400 font-bold">
                      No se encontró resultado
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Cambiar Vistas (Mes / Semana) */}
        <div className="flex bg-slate-100 p-0.5 rounded-lg border border-slate-200">
          <button
            onClick={() => setCalendarView('month')}
            className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${
              calendarView === 'month' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Mes
          </button>
          <button
            onClick={() => setCalendarView('week')}
            className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${
              calendarView === 'week' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Semana
          </button>
        </div>
      </div>
    </div>
  );
};
